import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useAuth } from '@/hooks/useAuth'
import { auth } from '@/config/firebase'

export const LoginPage = () => {
  const { signInWithGoogle, loading, error, clearError } = useAuth()
  const isDevMockAuth = import.meta.env.DEV && !auth

  const handleGoogleSignIn = async () => {
    clearError()
    try {
      await signInWithGoogle()
    } catch (error) {
      // Error is handled by the store
      console.warn('Google sign in failed:', error)
    }
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 bg-background relative overflow-hidden">
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-primary/5 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-accent-amber/5 rounded-full blur-3xl opacity-30" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="w-full max-w-md z-10"
      >
        <div className="text-center mb-8">
          <div className="h-14 w-14 bg-primary rounded-xl mx-auto flex items-center justify-center mb-6 shadow-lg shadow-primary/25">
            <span className="text-2xl font-bold text-white">CM</span>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-3">
            ContextMD<span className="text-primary">.</span>
          </h1>
          <p className="text-muted-foreground text-lg">
            Your startup&apos;s intelligent source of truth.
          </p>
        </div>

        <Card className="p-8 border-none bg-card/50 backdrop-blur-sm shadow-2xl">
          <Button
            className="w-full h-12 text-base bg-white text-black hover:bg-gray-100 border border-gray-200"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </>
            )}
          </Button>

          {error && (
            <p className="mt-4 text-sm text-destructive text-center">
              {error}
            </p>
          )}

          <p className="text-center text-xs text-muted-foreground mt-6">
            {isDevMockAuth
              ? 'Firebase is not configured locally. This will continue in test mode.'
              : 'Google sign-in only.'}
          </p>
        </Card>
      </motion.div>
    </div>
  )
}
